import React from 'react'
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import writtenNumber from 'written-number'

dayjs.locale('fr')
writtenNumber.defaults.lang = 'fr'

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = StyleSheet.create({
    page: {
        padding: 40,
        backgroundColor: '#FFFFFF',
        fontFamily: 'Courier',
    },
    section: {
        margin: 10,
        padding: 10,
        flexGrow: 1,
    },
    headerContainer: {
        marginBottom: 20,
    },
    header1: {
        fontSize: 24,
        textAlign: 'center',
        marginBottom: 5,
    },
    header2: {
        fontSize: 20,
        textAlign: 'center',
        marginBottom: 5,
    },
    line: {
        width: '100%',
        height: 1,
        backgroundColor: '#000000',
        marginVertical: 10,
    },
    leftAlignedParagraph: {
        fontSize: 12,
        textAlign: 'left',
        marginBottom: 20,
        width: '50%',
    },
    rightAlignedParagraph: {
        fontSize: 12,
        textAlign: 'left',
        alignSelf: 'flex-end',
        marginBottom: 20,
        width: '50%',
    },
    justifiedText: {
        fontSize: 12,
        textAlign: 'justify',
        marginBottom: 10,
    },
    legalText: {
        fontSize: 10,
        fontFamily: 'Courier-Oblique',
        textAlign: 'justify',
        marginTop: 20,
    },
})

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/**
 * Format a float as a French locale string, right-padded to targetLength.
 * e.g. 1234.5 → "1 234,50" with padding.
 */
const floatToString = (x, targetLength) =>
    x
        ?.toLocaleString('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
        // Narrow no-break space (U+202F) used by fr-FR as thousands separator
        .replace(/\u202f/g, '\u00a0')
        .padStart(targetLength) ?? ''

/**
 * Spell out a monetary amount in French.
 * e.g. 1234.50 → "mille deux cent trente-quatre euros et cinquante centimes"
 */
const floatToSpelled = (x) => {
    const euros = Math.trunc(x)
    const cents = Math.round(100 * (x % 1))
    return (
        writtenNumber(euros) +
        ' euro' +
        (euros > 1 ? 's' : '') +
        ' et ' +
        writtenNumber(cents) +
        ' centime' +
        (cents > 1 ? 's' : '')
    )
}

// ---------------------------------------------------------------------------
// Data fetching
//
// Deliberately separated from the PDF Document component so that:
//   1. The caller can handle loading / error states before invoking pdf().
//   2. @react-pdf/renderer components remain pure (no async inside the tree).
//   3. The helper is independently testable.
//
// Execution plan
// ──────────────
// Phase 1 (sequential): fetch lease + property  →  resolve property_id
// Phase 2 (parallel):   transactions | tenants | property_owners
//
// The three Phase-2 queries are independent once property_id is known, so
// Promise.all cuts wall-clock time by ~2/3 compared to sequential fetches.
// ---------------------------------------------------------------------------

/**
 * Fetch all data required to render a receipt PDF.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} leaseId
 * @param {string} periodFrom  ISO date string (YYYY-MM-DD)
 * @returns {Promise<ReceiptData>}
 * @throws {Error} with a human-readable message on any fetch failure
 */
export async function fetchReceiptData(supabase, leaseId, periodFrom) {
    // ── Phase 1: lease + property ────────────────────────────────────────────
    const { data: lease, error: leaseError } = await supabase
        .from('lease')
        .select(`
            *,
            property:property_id (*)
        `)
        .eq('id', leaseId)
        .single()

    if (leaseError) {
        throw new Error(`Impossible de charger le bail : ${leaseError.message}`)
    }
    if (!lease) {
        throw new Error(`Bail introuvable (id: ${leaseId})`)
    }

    const propertyId = lease.property_id

    // ── Phase 2: three independent queries in parallel ───────────────────────
    //
    // transactions_bucket!inner  → PostgREST inner join; the `.eq` filter on
    //   a related table is only honoured when using !inner (left join ignores it).
    const [
        { data: transactions, error: txError },
        { data: tenantRows,   error: tenantError },
        { data: ownerRows,    error: ownerError },
    ] = await Promise.all([
        supabase
            .from('transaction')
            .select(`
                value,
                period_to,
                on,
                transactions_bucket!inner (
                    category,
                    lease_id
                )
            `)
            .eq('period_from', periodFrom)
            .eq('transactions_bucket.lease_id', leaseId)
            .order('value', { ascending: true }),

        supabase
            .from('tenant')
            .select(`
                person:person_id (id, title, first_name, last_name),
                company:company_id (id, name)
            `)
            .eq('lease_id', leaseId),

        supabase
            .from('property_owner')
            .select(`
                person:owner_person_id (
                    id, title, first_name, last_name,
                    address_1, address_2, zip, city, country
                ),
                company:owner_company_id (
                    id, name,
                    address_1, address_2, zip, city, country
                )
            `)
            .eq('property_id', propertyId),
    ])

    if (txError)     throw new Error(`Impossible de charger les transactions : ${txError.message}`)
    if (tenantError) throw new Error(`Impossible de charger les locataires : ${tenantError.message}`)
    if (ownerError)  throw new Error(`Impossible de charger les propriétaires : ${ownerError.message}`)

    if (!transactions?.length) {
        throw new Error(
            `Aucune transaction trouvée pour ce bail et cette période (${periodFrom}).`
        )
    }

    // ── Transform: transaction items ─────────────────────────────────────────
    //
    // Replaces the SQL window functions (SUM OVER, MAX OVER) with a single JS
    // reduce pass. The shape of each item mirrors the original SQL result so
    // the PDF template below needs no changes.
    const totalValue = transactions.reduce((sum, t) => sum + (t.value ?? 0), 0)
    const periodTo   = transactions.reduce((max, t) => (t.period_to > max ? t.period_to : max), '')
    const paymentOn  = transactions.reduce((max, t) => (t.on        > max ? t.on        : max), '')

    // targetLength replicates: FLOOR(FLOOR(LOG10(total)) * (4/3)) + 4
    const targetLength =
        totalValue > 0 ? Math.floor(Math.floor(Math.log10(totalValue)) * (4.0 / 3.0)) + 4 : 8

    const transactionItems = transactions.map((t) => ({
        // Pad/truncate category to 16 chars, matching the original SUBSTR logic
        category:    (t.transactions_bucket.category + '                ').substring(0, 16),
        value:       t.value,
        // Window-function values — identical on every item, kept for template compatibility
        periodTo,
        on:          paymentOn,
        totalValue,
        targetLength,
    }))

    // ── Transform: tenant names ───────────────────────────────────────────────
    //
    // Merge persons and companies, sort by id (matches ORDER BY id in SQL),
    // then join with ", ".
    const allTenants = [
        ...(tenantRows ?? [])
            .filter((t) => t.person)
            .map((t) => ({
                id:   t.person.id,
                name: `${t.person.title} ${t.person.first_name} ${t.person.last_name}`,
            })),
        ...(tenantRows ?? [])
            .filter((t) => t.company)
            .map((t) => ({ id: t.company.id, name: t.company.name })),
    ].sort((a, b) => a.id.localeCompare(b.id))

    const tenants = allTenants.map((t) => t.name).join(', ')

    // ── Transform: owner names + address ─────────────────────────────────────
    //
    // Merge persons and companies, sort by id.
    // Address is taken from the first owner by id (FIRST_VALUE OVER in SQL).
    const allOwners = [
        ...(ownerRows ?? [])
            .filter((o) => o.person)
            .map((o) => ({
                id:        o.person.id,
                name:      `${o.person.title} ${o.person.first_name} ${o.person.last_name}`,
                address_1: o.person.address_1,
                address_2: o.person.address_2,
                zip:       o.person.zip,
                city:      o.person.city,
                country:   o.person.country,
            })),
        ...(ownerRows ?? [])
            .filter((o) => o.company)
            .map((o) => ({
                id:        o.company.id,
                name:      o.company.name,
                address_1: o.company.address_1,
                address_2: o.company.address_2,
                zip:       o.company.zip,
                city:      o.company.city,
                country:   o.company.country,
            })),
    ].sort((a, b) => a.id.localeCompare(b.id))

    const firstOwner = allOwners[0]
    const owners = {
        c:         allOwners.length,
        names:     allOwners.map((o) => o.name).join(', '),
        address_1: firstOwner?.address_1,
        address_2: firstOwner?.address_2,
        zip:       firstOwner?.zip,
        city:      firstOwner?.city,
        country:   firstOwner?.country,
    }

    return {
        transactionItems, // { category, value, periodTo, on, totalValue, targetLength }[]
        property: lease.property,
        tenants,           // comma-separated full names
        owners,            // { c, names, address_1, address_2, zip, city, country }
    }
}

// ---------------------------------------------------------------------------
// PDF Document component
//
// Props
// ─────
//   data        – result of fetchReceiptData(); contains all pre-fetched data
//   periodFrom  – ISO date string, used for header and body text
//
// This component is a pure renderer: no hooks, no async, no side-effects.
// All data must be resolved by the caller before passing it here.
// ---------------------------------------------------------------------------
const PdfReceipt = ({ data, periodFrom }) => {
    const { transactionItems, property, tenants, owners } = data

    // Convenience aliases matching the original template variable names
    const firstItem = transactionItems?.[0]

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.section}>

                    {/* ── Header ─────────────────────────────────────────── */}
                    <View style={styles.headerContainer}>
                        <View style={styles.line} />
                        <Text style={styles.header1}>QUITTANCE DE LOYER</Text>
                        <Text style={styles.header2}>
                            {property?.short_name} | {dayjs(periodFrom).format('YYYY-MM')}
                        </Text>
                        <View style={styles.line} />
                    </View>

                    {/* ── Landlord address (left) ─────────────────────────── */}
                    <View style={styles.leftAlignedParagraph}>
                        <Text>{owners?.names}</Text>
                        <Text>{owners?.address_1}</Text>
                        {owners?.address_2 ? <Text>{owners.address_2}</Text> : null}
                        <Text>{owners?.zip} {owners?.city}</Text>
                        <Text>{owners?.country}</Text>
                    </View>

                    {/* ── Tenant address (right) ──────────────────────────── */}
                    <View style={styles.rightAlignedParagraph}>
                        <Text>{tenants}</Text>
                        <Text>{property?.address_1}</Text>
                        {property?.address_2 ? <Text>{property.address_2}</Text> : null}
                        <Text>{property?.zip} {property?.city}</Text>
                        <Text>{property?.country}</Text>
                    </View>

                    {/* ── Date (right) ────────────────────────────────────── */}
                    <View style={styles.rightAlignedParagraph}>
                        <Text>Fait à {owners?.city}, le {dayjs().format('DD MMMM YYYY')}</Text>
                    </View>

                    {/* ── Rental address block ────────────────────────────── */}
                    <View style={styles.justifiedText}>
                        <Text style={{ textDecoration: 'underline' }}>
                            Adresse de la location :
                        </Text>
                        <Text>{property?.address_1}</Text>
                        {property?.address_2 ? <Text>{property.address_2}</Text> : null}
                        <Text>{property?.zip} {property?.city}</Text>
                        <Text>{property?.country}</Text>
                    </View>

                    {/* ── Declaration paragraph ───────────────────────────── */}
                    <Text style={styles.justifiedText}>
                        {owners?.c > 1 ? 'Nous' : 'Je'} soussigné
                        {owners?.c > 1 ? 's' : ''} {owners?.names}, propriétaire
                        {owners?.c > 1 ? 's' : ''} du logement désigné ci-dessus,{' '}
                        déclar{owners?.c > 1 ? 'ons' : 'e'} avoir reçu de {tenants} la somme
                        de {floatToSpelled(firstItem?.totalValue ?? 0)}{' '}
                        ({floatToString(firstItem?.totalValue, firstItem?.targetLength)} €),
                        au titre du paiement du loyer et des charges pour la période de location
                        du {dayjs(periodFrom).format('DD MMMM YYYY')} au{' '}
                        {dayjs(firstItem?.periodTo).format('DD MMMM YYYY')}, et lui en
                        donn{owners?.c > 1 ? 'ons' : 'e'} quittance, sous réserve de
                        tous {owners?.c > 1 ? 'nos' : 'mes'} droits.
                    </Text>

                    {/* ── Payment breakdown ───────────────────────────────── */}
                    <View style={styles.justifiedText}>
                        <Text style={{ textDecoration: 'underline' }}>
                            Détail du règlement :
                        </Text>
                        {transactionItems.map((item, idx) => (
                            <Text key={idx}>
                                {item.category} :{' '}
                                {floatToString(item.value, firstItem?.targetLength)} €
                            </Text>
                        ))}
                        <Text>
                            {'Total            '}:{' '}
                            {floatToString(firstItem?.totalValue, firstItem?.targetLength)} €
                        </Text>
                        <Text>
                            {'Date du paiement '}:{' '}
                            {dayjs(firstItem?.on).format('DD MMMM YYYY')}
                        </Text>
                    </View>

                    {/* ── Signature line ──────────────────────────────────── */}
                    <View style={styles.rightAlignedParagraph}>
                        <Text>{owners?.names}</Text>
                    </View>

                    {/* ── Legal disclaimer ────────────────────────────────── */}
                    <Text style={styles.legalText}>
                        Cette quittance annule tous les reçus qui auraient pu être établis
                        précédemment en cas de paiement partiel du montant du présent terme.
                        Elle est à conserver pendant trois ans par le locataire (loi n° 89-462
                        du 6 juillet 1989 : art. 7-1).
                    </Text>

                </View>
            </Page>
        </Document>
    )
}

export default PdfReceipt